import { ApolloQueryResult } from '@apollo/client';
import ButtonGroup from '@atlaskit/button/button-group';
import LoadingButton from '@atlaskit/button/loading-button';
import Button from '@atlaskit/button/standard-button';
import Form, { ErrorMessage, Field, FormFooter, FormHeader, FormSection } from '@atlaskit/form';
import SectionMessage from '@atlaskit/section-message';
import Textfield from '@atlaskit/textfield';
import { analytics, AnalyticsTrackAction, AnalyticsTrackSubject } from '@atlassian/temp-analytics';
import { isStatuspage } from '@atlassian/cssxp-utils-product';
import { EntitlementEdition, getUserTier, constructCustomOrderRequest } from '@atlassian/cssxp-utils-entitlement';
import {
  ErrorComponents,
  errorNotificationBuilder,
  FormattedDate,
  InfoPanel,
  NotificationTypes,
  successNotificationBuilder,
  useFeatureToggle,
  useFormSubmit,
  useNotification,
  useSidebar,
  withApollo,
} from '@css-xp/base';
//@ts-ignore
import EntitlementIcon from '@css-xp/base/entitlement-icon';
import {
  currentEntitlementType,
  getAllRemovedPromotions,
  getBilledForColumnData,
  getChargeElement,
  getLinkedEntitlementsDetails,
  getParentContainerId,
  getReadablePromotionTitle,
  getReadablePromotionValue,
  getScheduleChangesDescription,
  LinkedEntitlementsTable,
} from '@css-xp/base/entitlement-utils';
import { ChargeQuantity, Promotion } from '@css-xp/graphtypes';
import {
  EntitlementDetails,
  LinkedDestinationEntitlement,
  LinkedEntitlementTypes,
  SubscriptionScheduleAction,
  LinkedProductTypes,
} from '@css-xp/graphtypes/src/generated-types-new';
import { updateEntitlementPlan, updateEntitlementPlanV2 } from '@atlassian/temp-proxies';
import { OMSMetadata, OrderItemType } from '@atlassian/cssxp-types-entitlement'; //'../tabs/schedule-tab/src/schedule-tab';
import { UpdateOrderRequest, CustomOrderEntitlement } from '@atlassian/cssxp-typescommon';
import {
  ENABLE_ENTITLEMENT_RELATIONSHIP_FEATURE,
  sentenceCase,
  OMS_DELAY_FOR_ENTITLEMENT_RELATIONSHIP_FEATURE,
  ENABLE_OMS_SERVICE_V2,
} from '@atlassian/cssxp-utils-generic';
import React, { useEffect, useMemo, useState } from 'react';
import { TransferredAccountDetails } from '..';
import { ShowUpdatedText } from '../../../../entitlement-page/src/tabs/schedule-tab/src/schedule-tab';
import { WarningDialog } from '../../warning-dialog';
import { UndoScheduledCancelAppsTable } from '../../../components/undo-scheduled-cancel-apps-table';
import './entitlement-cancel-scheduled-subscription-pane.scss';

interface Props {
  changeDate: number | null;
  changeAction?: SubscriptionScheduleAction | null;
  oldValues: ScheduledChangeComparisonFields;
  newValues: ScheduledChangeComparisonFields;
  scheduledPromotions?: Promotion[];
  scheduledQuantity?: ChargeQuantity[];
  scheduledChanges?: EntitlementDetails['scheduledChanges'];
  units?: string | null;
  entitlementNumber: string;
  entitlementId: string;
  productId: string;
  productName: string;
  refetch: () => Promise<ApolloQueryResult<unknown>>;
  promotions?: Promotion[];
  entitlement: EntitlementDetails;
}

interface ScheduledChangeComparisonFields {
  offering?: string | null;
  pricingPlan?: string | null;
  billingFrequency?: string | null;
}

enum OrderType {
  AMENDMENT_ORDER = 'AMENDMENT_ORDER',
}

const viewData = {
  title: 'Undo changes',
  error_message: 'Unable to cancel the scheduled billing changes due to the following reason',
  required_message: 'When you undo a scheduled change, it will be applied immediately',
  button_name: 'Cancel Subscription',
};

interface FormData {
  supportTicketNumber?: string | null;
}

export const CancelScheduledSubscription = ({
  entitlementNumber,
  entitlementId,
  changeDate,
  changeAction,
  oldValues,
  newValues,
  scheduledPromotions,
  scheduledQuantity,
  units,
  productId,
  scheduledChanges,
  productName,
  refetch,
  promotions,
  entitlement,
}: Props) => {
  const { closeSidebar } = useSidebar();
  const [isEnabled] = useFeatureToggle();
  const isEntRelationFeatureEnable = isEnabled(ENABLE_ENTITLEMENT_RELATIONSHIP_FEATURE);
  const isOMSDelayFeatureEnable = isEnabled(OMS_DELAY_FOR_ENTITLEMENT_RELATIONSHIP_FEATURE);
  const isOmsServiceV2Enabled = isEnabled(ENABLE_OMS_SERVICE_V2);
  const [linkedEntitlementsData, setLinkedEntitlementsData] = useState<Array<LinkedDestinationEntitlement>>([]);
  const [isConfirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [loadingLinkedEntitlement, setLoadingLinkedEntitlements] = useState(false);

  const { error: formError, isProcessing, submitForm, complete } = useFormSubmit<any, any>({
    submitFormAction: isOmsServiceV2Enabled ? updateEntitlementPlanV2 : updateEntitlementPlan,
  });

  useEffect(() => {
    if (complete) {
      closeSidebar();
      refetch();
      analytics.sendTrackEvent({
        attributes: {
          cancellationType: 'Scheduled',
        },
        action: AnalyticsTrackAction.CANCEL,
        actionSubject: AnalyticsTrackSubject.CANCEL_SUBSCRIPTION,
      });
    }
  }, [closeSidebar, complete, refetch]);

  const isUndoRelationEnabled = useMemo(() => {
    if (isEntRelationFeatureEnable && (entitlement?.relatesToEntitlements || entitlement?.relatesFromEntitlements)) {
      if (
        changeAction === SubscriptionScheduleAction.CANCEL ||
        changeAction === SubscriptionScheduleAction.UPDATE_CANCEL
      ) {
        return isOMSDelayFeatureEnable;
      }

      // change in transaction account id, should enable relations
      if (
        scheduledChanges?.transactionAccountId &&
        scheduledChanges?.transactionAccountId !== entitlement.transactionAccountId
      ) {
        return true;
      }
      // change in transaction account id, should enable relations
      if (scheduledChanges?.invoiceGroupId && scheduledChanges?.invoiceGroupId !== entitlement.billingGroupId) {
        return true;
      }
    }

    return false;
  }, [isEntRelationFeatureEnable, scheduledChanges, entitlement, changeAction, isOMSDelayFeatureEnable]);

  const { sendSuccessNotification, notificationSent, setNotificationSent } = useNotification();
  if (complete && !formError && !notificationSent) {
    const notificationOptions = successNotificationBuilder()
      .isVersion(NotificationTypes.FLAG)
      .withId('cancel-scheduled-subscription-success')
      .withHeader('Task complete')
      .withMessage('Subscription changes have been undone')
      .withDismissAfter(5000)
      .build();
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
  } else if (formError && !complete && notificationSent) {
    const notificationOptions = errorNotificationBuilder()
      .isVersion(NotificationTypes.FLAG)
      .withId('cancel-scheduled-subscription-error')
      .withHeader('We’re unable to undo the change')
      .withMessage(
        <div>
          Looks like something went wrong - try again in a few minutes. Still having trouble? Contact the{' '}
          <strong>#help-advocate-central</strong> channel
        </div>
      )
      .withDismissAfter(5000)
      .build();
    sendSuccessNotification(notificationOptions);
    setNotificationSent(true);
  }

  const constructRequestData = (entitlementId: string, formData: FormData) => {
    const requestData: UpdateOrderRequest = {
      metadata: {
        [OMSMetadata.SUPPORT_TICKET_NUMBER]: formData.supportTicketNumber ?? null,
      },
      entitlementId: entitlementId,
      orderType: OrderType.AMENDMENT_ORDER,
      units: isStatuspage(entitlement?.productId)
        ? (units as string)
        : getUserTier(entitlement?.subscription)?.toString(),
      isScheduleCancellation: true,
      productId: productId ? productId : '', //@ts-ignore
      chargeQuantities: scheduledChanges?.chargeQuantities || [],
      chargeElement: getChargeElement(entitlement?.productId as string),
      nextBillingAnchorTimestamp: entitlement?.nextBillingTimestamp
        ? `${entitlement?.nextBillingTimestamp}`
        : undefined,
    };

    if (isUndoRelationEnabled) {
      requestData.linkedEntitlements = linkedEntitlementsData.length
        ? getLinkedEntitlementsDetails(entitlement, linkedEntitlementsData)
        : [];
    }

    if (isOmsServiceV2Enabled) {
      const entitlementsList: CustomOrderEntitlement[] = [
        {
          entitlementId: entitlement.id,
          productId: entitlement?.productId as string,
          offeringId: entitlement?.offeringId as string,
        },
      ];

      if (isUndoRelationEnabled) {
        linkedEntitlementsData?.forEach((linkedEntitlement) => {
          entitlementsList.push({
            entitlementId: linkedEntitlement.entitlementId,
            productId: linkedEntitlement.productId as string,
            offeringId: linkedEntitlement.offeringId as string,
          });
        });
      }

      const constructV2Request = {
        metadata: {
          [OMSMetadata.SUPPORT_TICKET_NUMBER]: formData.supportTicketNumber ?? null,
        },
        orderType: OrderItemType.AMENDMENT_ORDER,
        entitlements: entitlementsList,
        transactionAccountId: entitlement?.transactionAccountId as string,
        invoiceGroupId: entitlement?.billingGroupId as string,
      };
      return constructCustomOrderRequest(constructV2Request);
    }

    return requestData;
  };

  const handleFormSubmit = (formData: FormData) => {
    if (isUndoRelationEnabled) {
      setConfirmDialogOpen(true);
    } else {
      const requestData = constructRequestData(entitlementId, formData);
      submitForm(requestData);
    }
  };

  const handleConfirmDialog = (formData: FormData) => {
    setConfirmDialogOpen(false);
    const requestData = constructRequestData(entitlementId, formData);
    submitForm(requestData);
  };

  const allRemovedPromotions = getAllRemovedPromotions(promotions, scheduledPromotions);

  const checkIfTransferOccured =
    !!scheduledChanges?.invoiceGroupId &&
    !!scheduledChanges?.transactionAccountId &&
    (changeAction === SubscriptionScheduleAction.TRANSFER ||
      changeAction === SubscriptionScheduleAction.UPDATE_TRANSFER);

  const lastParentShouldCheckOrNot = useMemo(() => {
    if (entitlement.productType === LinkedProductTypes.JIRA) {
      return true;
    } else if (entitlement.productType === LinkedProductTypes.CONFLUENCE) {
      return entitlement.edition?.toUpperCase() === EntitlementEdition.PREMIUM;
    } else return false;
  }, [entitlement]);

  return (
    <div className="scheduled-subscription-form">
      <Form onSubmit={handleFormSubmit}>
        {({ formProps, getState }) => (
          <form {...formProps}>
            <FormHeader title={viewData.title}></FormHeader>
            <EntitlementIcon productId={productId} className="header-icon" isCloud={true} />
            <span className={'schedule_header'}>{productName + ' ' + entitlementNumber}</span>
            <SectionMessage appearance="warning">
              <p>{viewData.required_message}</p>
            </SectionMessage>
            <FormSection>
              {!!formError && (
                <InfoPanel
                  type="error"
                  message={
                    <ErrorComponents.ErrorMessageOnInfoPanel title={viewData.error_message} message={formError} />
                  }
                />
              )}
            </FormSection>
            {isUndoRelationEnabled &&
              (changeAction === SubscriptionScheduleAction.CANCEL ||
              changeAction === SubscriptionScheduleAction.UPDATE_CANCEL ? (
                <div className="linked-entitlement-table-container">
                  <UndoScheduledCancelAppsTable
                    entitlementId={entitlement?.id}
                    isApp={!!entitlement.isApp}
                    parentContainerID={getParentContainerId(entitlement)}
                    currentEntitlementType={currentEntitlementType(entitlement)}
                    destinationEntitlementType={
                      entitlement?.isSandbox
                        ? [LinkedEntitlementTypes.SANDBOX_APP_ENTITLEMENT]
                        : [LinkedEntitlementTypes.PROD_ENTITLEMENT_APP]
                    }
                    findProducts={false}
                    findApps={true}
                    productType={entitlement.productType}
                    checkForLastParentExistence={lastParentShouldCheckOrNot}
                    message1={'You can also undo the cancellation of these linked subscriptions:'}
                    message2={'Select apps you no longer want to cancel'}
                    setLoading={setLoadingLinkedEntitlements}
                    updateSelectedLinkedEntitlements={setLinkedEntitlementsData}
                  />
                </div>
              ) : (
                <div className="linked-entitlement-table-container">
                  <LinkedEntitlementsTable
                    entitlementId={entitlement?.id}
                    parentContainerID={getParentContainerId(entitlement)}
                    currentEntitlementType={currentEntitlementType(entitlement)}
                    destinationEntitlementType={[
                      LinkedEntitlementTypes.PROD_ENTITLEMENT_APP,
                      LinkedEntitlementTypes.PROD_ENTITLEMENT_PRODUCT,
                      LinkedEntitlementTypes.SANDBOX_APP_ENTITLEMENT,
                      LinkedEntitlementTypes.SANDBOX_PRODUCT_ENTITLEMENT,
                    ]}
                    findProducts={true}
                    findApps={true}
                    productType={entitlement.productType}
                    message={
                      'This action will automatically reverse the same scheduled changes for these subscriptions:'
                    }
                    updateLinkedEntitlements={setLinkedEntitlementsData}
                    setLoading={setLoadingLinkedEntitlements}
                  />
                </div>
              ))}
            <FormSection>
              <label className={'scheduled-label'}>Change effective</label>
              <div className={'disabled-div'}>
                <FormattedDate date={new Date(changeDate ?? '').toISOString()} showTime={true} />
              </div>
            </FormSection>
            <FormSection>
              <label className={'scheduled-label'}>Description</label>
              <div className={'disabled-div'}>{getScheduleChangesDescription(changeAction)}</div>
            </FormSection>
            {checkIfTransferOccured && (
              <TransferredAccountDetails entitlementId={entitlementId} scheduledChanges={scheduledChanges} />
            )}
            <FormSection>
              <label className={'scheduled-label'}>Plan</label>
              <div className={'disabled-div'}>
                <ShowUpdatedText before={sentenceCase(oldValues.offering)} after={sentenceCase(newValues.offering)} />
              </div>
            </FormSection>
            <FormSection>
              <label className={'scheduled-label'}>Pricing</label>
              <div className={'disabled-div'}>
                <ShowUpdatedText
                  before={sentenceCase(oldValues.pricingPlan)}
                  after={sentenceCase(newValues.pricingPlan)}
                />
              </div>
            </FormSection>
            <FormSection>
              <label className={'scheduled-label'}>Billing cycle</label>
              <div className={'disabled-div'}>
                <ShowUpdatedText
                  before={sentenceCase(oldValues.billingFrequency)}
                  after={sentenceCase(newValues.billingFrequency)}
                />
              </div>
            </FormSection>
            <FormSection>
              <label className={'scheduled-label'}>Billed for</label>
              <div className={'disabled-div'}>
                {scheduledQuantity && scheduledQuantity.length > 0 && (
                  <ShowUpdatedText
                    before={units ? units + ' ' + getBilledForColumnData(productId, oldValues.offering) : ''}
                    after={
                      `${scheduledQuantity[0].quantity}` + ' ' + getBilledForColumnData(productId, newValues.offering)
                    }
                  />
                )}
              </div>
            </FormSection>
            <FormSection>
              <label className={'scheduled-label'}>Discount removed</label>
              <div className={'disabled-div'}>
                {((scheduledPromotions && scheduledPromotions?.length > 0) || allRemovedPromotions.length > 0) &&
                  allRemovedPromotions.map((promotion: Promotion, index: number) => {
                    return (
                      <div key={index} className={'strike'}>
                        <p className={'scheduled-title'}>{getReadablePromotionTitle(promotion)}</p>
                        <p className={'scheduled-value'}>{getReadablePromotionValue(promotion)}</p>
                      </div>
                    );
                  })}
              </div>
            </FormSection>
            <FormSection>
              <label className={'scheduled-label'}>Discount added</label>
              <div className={'disabled-div'}>
                {scheduledPromotions &&
                  scheduledPromotions?.length > 0 &&
                  scheduledPromotions
                    .filter((schedule) => schedule.status === 'ACTIVE')
                    .map((scheduledPromotion: Promotion, index: number) => {
                      return (
                        <div key={index}>
                          <p className={'scheduled-title'}>{getReadablePromotionTitle(scheduledPromotion)}</p>
                          <p className={'scheduled-value'}>{getReadablePromotionValue(scheduledPromotion)}</p>
                        </div>
                      );
                    })}
              </div>
            </FormSection>
            <FormSection>
              <Field
                name="supportTicketNumber"
                label="Support ticket number"
                isRequired={true}
                validate={(value: any) => (value && value.length > 0 ? undefined : 'INVALID')}
              >
                {({ fieldProps, error }) => (
                  <>
                    <Textfield
                      {...fieldProps}
                      name="supportTicketNumber"
                      placeholder="Add GSAC ticket number"
                      testId="support-ticket-number"
                    />
                    {error && <ErrorMessage>Enter a valid ticket number</ErrorMessage>}
                  </>
                )}
              </Field>
            </FormSection>
            <FormSection></FormSection>
            <FormFooter align="end">
              <ButtonGroup>
                <Button appearance="subtle" testId="cancel" onClick={() => closeSidebar()}>
                  Cancel
                </Button>
                <LoadingButton
                  appearance="primary"
                  type="submit"
                  isLoading={isProcessing}
                  testId="cancel-scheduled-subscription"
                  isDisabled={loadingLinkedEntitlement}
                >
                  Undo changes
                </LoadingButton>
              </ButtonGroup>
            </FormFooter>
            {isConfirmDialogOpen && (
              <WarningDialog
                isOpen={isConfirmDialogOpen}
                onClose={() => setConfirmDialogOpen(false)}
                onConfirm={() => handleConfirmDialog(getState().values)}
                header={'Are you sure?'}
                description={
                  <span>
                    Doing this will reverse the same scheduled changes for all the subscriptions listed on the previous
                    screen.
                  </span>
                }
                okLabel={'Confirm'}
              />
            )}
          </form>
        )}
      </Form>
    </div>
  );
};

export default withApollo(CancelScheduledSubscription);
